import {
  BENEFICIARY_CONSEQUENCES,
  BENEFICIARY_KINDS,
  BENEFICIARY_LABELS,
  type BeneficiaryKind,
} from '@hvac/contracts';
import { cn } from '@hvac/ui';

/**
 * One question, asked before any other.
 *
 * A bookkeeper does not look at a $4.99 energy drink and think "which
 * category". They think "who was that for", and the account follows. The same
 * purchase is an expense, a draw, a taxable benefit or a promotion depending
 * on nothing but the answer, so this question leads the line and the account
 * fields sit underneath it.
 *
 * Every choice states its consequence in the same breath as its label,
 * because a person choosing "The owner personally" is entitled to know they
 * are choosing "not deductible, no credit claimed" before they click, not
 * after their return is filed.
 */

export function WhoBenefited({
  value,
  onChange,
  disabled = false,
  reason = null,
}: {
  value: BeneficiaryKind;
  onChange: (next: BeneficiaryKind) => void;
  disabled?: boolean;
  /** Why it was attributed this way, when something already decided. */
  reason?: string | null;
}) {
  return (
    <fieldset className="sm:col-span-2">
      <legend className="text-sm font-medium text-content">Who was this for?</legend>
      <p className="mt-0.5 text-xs text-content-muted">
        The account follows from this, never the other way round.
      </p>

      <div className="mt-2 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Who was this for">
        {BENEFICIARY_KINDS.map((kind) => {
          const selected = kind === value;
          return (
            <button
              key={kind}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(kind)}
              className={cn(
                'rounded-[var(--radius-control)] border px-3 py-1.5 text-sm transition-[background-color,border-color,color,transform] duration-[var(--duration-quick)] ease-[var(--ease-spring)]',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
                selected
                  ? 'border-brand-500 bg-brand-50 font-medium text-brand-700'
                  : 'border-border-strong bg-surface text-content-muted hover:border-brand-300 hover:text-content',
                disabled ? 'cursor-not-allowed opacity-60' : 'hover:-translate-y-px',
              )}
            >
              {BENEFICIARY_LABELS[kind]}
            </button>
          );
        })}
      </div>

      <p key={value} className="animate-rise mt-2 text-sm text-content-muted" aria-live="polite">
        {BENEFICIARY_CONSEQUENCES[value]}
        {reason === null || reason.trim() === '' ? null : (
          <span className="block text-xs text-content-subtle">{reason}</span>
        )}
      </p>
    </fieldset>
  );
}
